import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '@/supabaseClient'
import { useTheme } from '@/contexts/ThemeContext'
import GameDisambiguationModal from './GameDisambiguationModal'
import { useSteamIntegration } from './useSteamIntegration'
import { useGameManagement } from './useGameManagement'
import { useUserProfile } from './useUserProfile'
import { useToast } from './useToast'
import UserAccountInfo from './UserAccountInfo'
import SteamGamesList from './SteamGamesList'
import SettingsSection from './SettingsSection'

const ProfilePage = ({ isDemoUser = false }: { isDemoUser?: boolean }) => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleCount, setVisibleCount] = useState(30)
  const [steamImportNotice, setSteamImportNotice] = useState<string | null>(
    null
  )
  const steamMockEnabled =
    import.meta.env?.VITE_STEAM_MOCK_ENABLED === 'true'
  const demoMode = Boolean(isDemoUser)

  const {
    user,
    setUser,
    loading,
    error,
    setError,
    isEditingName,
    editedName,
    setEditedName,
    updateUserName,
    cancelNameEdit,
    startNameEdit,
  } = useUserProfile()

  const { showSuccessToast, showErrorToast } = useToast()

  const { theme, setTheme } = useTheme()

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  const handleThemeChange = async (newTheme: string) => {
    await setTheme(newTheme as 'sunrise' | 'sunrise-dark')
  }

  const {
    steamGames,
    steamProfile,
    loadingGames,
    selectedGames,
    handleConnectToSteam,
    handleOpenIDResponse,
    hydrateSteamFromProfile,
    handleDisconnectSteam,
    error: steamError,
    setError: setSteamError,
    setSelectedGames,
  } = useSteamIntegration(user, setUser)

  useEffect(() => {
    if (steamError) {
      setError(steamError)
      setSteamError(null)
    }
  }, [steamError, setError, setSteamError])

  const {
    userLibraryGames,
    disambiguationOpen,
    disambiguationGames,
    fetchUserLibrary,
    toggleGameSelection,
    onSelectGame,
    onCancelDisambiguation,
  } = useGameManagement(
    user,
    steamGames,
    selectedGames,
    setSelectedGames,
    setError
  )

  const startSteamImport = async (appIds?: number[]) => {
    if (demoMode) {
      setSteamImportNotice('Steam import is disabled in the demo.')
      return
    }
    try {
      setSteamImportNotice(null)
      const steamId = user?.steamId || steamProfile?.steamId
      if (!steamId) {
        setSteamImportNotice('No Steam account connected.')
        return
      }
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) {
        setSteamImportNotice('Missing session. Please log in again.')
        return
      }
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/steam/imports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            steamId,
            appIds,
          }),
        }
      )

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null)
        setSteamImportNotice(
          errorPayload?.error || 'Failed to start Steam import.'
        )
        return
      }

      const payload = await response.json()
      if (payload?.jobId) {
        try {
          localStorage.setItem('steamImportLastJobId', payload.jobId)
          localStorage.setItem(
            'steamImportLastJobStartedAt',
            new Date().toISOString()
          )
        } catch (storageError) {
          console.warn('[steam-import] failed to persist job id', storageError)
        }
      }
      setSteamImportNotice(
        'Steam import started. You can leave this page anytime.'
      )
      setSelectedGames(new Set())
    } catch (error) {
      console.error('Error starting Steam import:', error)
      setSteamImportNotice('Failed to start Steam import.')
    }
  }

  useEffect(() => {
    if (user?.id && steamGames.length > 0) {
      fetchUserLibrary()
    }
  }, [user, steamGames, fetchUserLibrary])

  // Update the useEffect that handles the OpenID response
  useEffect(() => {
    if (demoMode) return
    const urlParams = new URLSearchParams(window.location.search)
    // Check if we have an OpenID response from Steam
    if (urlParams.has('openid.claimed_id')) {
      handleOpenIDResponse()
    }
  }, [demoMode, handleOpenIDResponse])

  useEffect(() => {
    if (demoMode) return
    const urlParams = new URLSearchParams(window.location.search)
    if (!urlParams.has('openid.claimed_id')) {
      hydrateSteamFromProfile()
    }
  }, [demoMode, hydrateSteamFromProfile])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center">
        <div className="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{error}</span>
          <button
            className="btn btn-sm"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center">
        <div className="alert alert-warning">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>No user data found. Try signing out and back in.</span>
          <button className="btn btn-sm" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Success Toast */}
      <div id="success-toast" className="toast toast-top toast-end hidden">
        <div className="alert alert-success">
          <span>Games added to your library!</span>
        </div>
      </div>

      {/* Error Toast */}
      <div id="error-toast" className="toast toast-top toast-end hidden">
        <div className="alert alert-error">
          <span>
            Something went wrong while adding your games. Please try again.
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold font-serif mb-8">Settings</h1>

        <UserAccountInfo
          user={user}
          isEditingName={isEditingName}
          editedName={editedName}
          setEditedName={setEditedName}
          onUpdateName={updateUserName}
          onCancelEdit={cancelNameEdit}
          onStartEdit={startNameEdit}
          onShowSuccessToast={showSuccessToast}
          onShowErrorToast={showErrorToast}
        />

        {demoMode ? (
          <div className="alert bg-base-200 text-base-content/80 border border-base-300 mb-8">
            Steam integration is disabled in the demo. Create an account to
            connect Steam.
          </div>
        ) : (
          <>
            {/* Connect to Steam Section */}
            <div className="card bg-base-200 shadow-xl mb-8">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="card-title">Steam Connection</h2>
                  {steamMockEnabled && (
                    <span className="badge badge-warning badge-sm">
                      Steam mock enabled
                    </span>
                  )}
                </div>
                {loadingGames ? (
                  <div className="flex justify-center">
                    <div className="loading loading-spinner loading-lg"></div>
                  </div>
                ) : steamGames.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-success">✓ Connected to Steam</p>
                        <p className="text-sm opacity-70">
                          {steamGames.length} games found
                        </p>
                      </div>
                      <button
                        className="btn btn-error btn-sm"
                        onClick={handleDisconnectSteam}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleConnectToSteam}
                  >
                    Connect to Steam
                  </button>
                )}
              </div>
            </div>

            {/* Steam Games Section */}
            {(user?.steamId || steamProfile?.steamId) && (
              <div className="card bg-base-200 shadow-xl mb-8">
                <div className="card-body">
                  <h2 className="card-title mb-4">Steam Games</h2>
                  <p className="text-sm opacity-70 mb-4">
                    Your Steam games. Click any to add it to your library — or add them all at once.
                  </p>
                  {steamImportNotice && (
                    <div className="alert alert-info mb-4">
                      <span>{steamImportNotice}</span>
                    </div>
                  )}
                  <SteamGamesList
                    steamGames={steamGames}
                    userLibraryGames={userLibraryGames}
                    selectedGames={selectedGames}
                    searchTerm={searchTerm}
                    visibleCount={visibleCount}
                    loadingGames={loadingGames}
                    setSearchTerm={setSearchTerm}
                    setSelectedGames={setSelectedGames}
                    setVisibleCount={setVisibleCount}
                    toggleGameSelection={toggleGameSelection}
                    onStartImport={startSteamImport}
                  />
                </div>
              </div>
            )}
          </>
        )}

        <SettingsSection
          theme={theme}
          onThemeChange={handleThemeChange}
          onSignOut={handleSignOut}
        />
      </div>
      {/* Disambiguation Modal for IGDB matches */}
      <GameDisambiguationModal
        open={disambiguationOpen}
        games={disambiguationGames}
        onSelect={onSelectGame}
        onCancel={onCancelDisambiguation}
      />
    </div>
  )
}

{
  /* Disambiguation Modal for IGDB matches */
}
export default ProfilePage
